

import React, { useState, useEffect, useRef } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Activity,
  AlertCircle,
  Gauge,
  Ruler,
  Settings,
  Eye,
  Box,
  ArrowUp,
  Wifi,
  WifiOff,
  RefreshCw,
  Package,
  Info
} from "lucide-react";
import { MATERIAL_SPECIFICATIONS, getMaterialSpecs, isWithinSpec, getDefectType } from '../../Materialspecs';

const WidthInspectionDashboard = () => {
  const [currentWidth, setCurrentWidth] = useState(0.552);
  const [widthHistory, setWidthHistory] = useState([]);
  const [defectCount, setDefectCount] = useState(0);
  const [totalMeasurements, setTotalMeasurements] = useState(0);
  const [speed] = useState(12.5);
  const [alarmHistory, setAlarmHistory] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [materialSpecs, setMaterialSpecs] = useState({
    min: 0.5,
    max: 0.6,
    target: 0.55
  });

  const API_URL = "https://oyplm85fk3.execute-api.us-east-1.amazonaws.com/prod/realtime";
  const FETCH_INTERVAL = 2000; // Fetch every 2 seconds

  // Fetch real-time data from API
  const fetchRealtimeData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}?limit=20&minutes=10`);
      const data = await response.json();
      
      if (data.success && data.data) {
        setApiData(data);
        setIsConnected(true);
        setLastUpdate(new Date());
        
        // Update statistics
        // if (data.statistics) {
        //   setTotalMeasurements(data.statistics.count || 0);
          
        //   // Update current width with latest value
        //   if (data.statistics.latest !== null) {
        //     setCurrentWidth(data.statistics.latest);
        //   }
        // }
// Update statistics
if (data.statistics) {
  setTotalMeasurements(data.statistics.count || 0);
}

// Update current width with latest value from actual data (more reliable)
if (data.data && data.data.length > 0) {
  const latestReading = data.data[0];
  const latestWidth = latestReading.width_meters || latestReading.measure_value;
  if (latestWidth !== null && latestWidth !== undefined) {
    setCurrentWidth(latestWidth);
  }
}
        
        // Update material information from latest data
        if (data.data && data.data.length > 0) {
          const latestData = data.data[0];
          const sensorType = latestData.sensor_type;
          
          // Update current material using the complete material specs
          if (sensorType && MATERIAL_SPECIFICATIONS[sensorType]) {
            const materialData = MATERIAL_SPECIFICATIONS[sensorType];
            const specs = getMaterialSpecs(sensorType);
            
            setCurrentMaterial({
              code: sensorType,
              description: materialData.name,
              target: specs.target_m,  // Use meters for API compatibility
              min: specs.min_m,
              max: specs.max_m,
              targetMM: materialData.film_width,
              minMM: materialData.min,
              maxMM: materialData.max
            });
            
            setMaterialSpecs({
              min: specs.min_m,
              max: specs.max_m,
              target: specs.target_m
            });
          } else {
            // Default if material not found
            const defaultSpecs = getMaterialSpecs('UNKNOWN');
            setCurrentMaterial({
              code: sensorType || 'UNKNOWN',
              description: 'Material not in database',
              target: defaultSpecs.target_m,
              min: defaultSpecs.min_m,
              max: defaultSpecs.max_m,
              targetMM: defaultSpecs.film_width,
              minMM: defaultSpecs.min,
              maxMM: defaultSpecs.max
            });
            
            setMaterialSpecs({
              min: defaultSpecs.min_m,
              max: defaultSpecs.max_m,
              target: defaultSpecs.target_m
            });
          }
          
          // Update width history
          const historyData = data.data.map(item => {
            const widthMeters = item.width_meters || item.measure_value || 0;
            const materialCode = item.sensor_type || 'UNKNOWN';
            const defectInfo = getDefectType(widthMeters, materialCode);
            
            return {
              time: new Date(item.time).toLocaleTimeString(),
              width: widthMeters,
              widthMM: widthMeters * 1000,
              device_id: item.device_id || 'unknown',
              location: item.location || 'unknown',
              sensor_type: materialCode,
              isDefect: !isWithinSpec(widthMeters, materialCode),
              defectType: defectInfo?.type || null,
              deviation: defectInfo?.deviation || 0
            };
          });
          
          setWidthHistory(historyData.reverse());
          
          // Count defects
          const outOfSpec = historyData.filter(d => d.isDefect);
          setDefectCount(outOfSpec.length);
          
          // Update alarms for recent violations
          const newAlarms = outOfSpec.slice(0, 5).map(item => ({
            id: Date.now() + Math.random(),
            time: item.time,
            width: item.width,
            widthMM: item.widthMM,
            device: item.device_id,
            location: item.location,
            material: item.sensor_type,
            type: item.defectType,
            deviation: item.deviation
          }));
          
          if (newAlarms.length > 0) {
            setAlarmHistory(newAlarms);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch and set up interval
  useEffect(() => {
    fetchRealtimeData();
    const interval = setInterval(fetchRealtimeData, FETCH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const isInSpec = currentWidth >= materialSpecs.min && currentWidth <= materialSpecs.max;
  const qualityRate = totalMeasurements > 0 
    ? ((totalMeasurements - defectCount) / totalMeasurements * 100).toFixed(1)
    : 100;

  const getGaugeColor = (width) => {
    if (width < materialSpecs.min || width > materialSpecs.max) return "#ef4444";
    if (Math.abs(width - materialSpecs.target) < 0.002) return "#10b981";
    return "#f59e0b";
  };

  const getGaugePosition = (width) => {
    const range = 0.1; // Adjusted range for better visualization
    const center = materialSpecs.target;
    const normalized = (width - (center - range/2)) / range;
    return Math.max(0, Math.min(100, normalized * 100));
  };

  return (
    <div className="min-h-screen md:h-[calc(100vh-64px)] bg-gradient-to-b from-slate-950 to-slate-900 text-white overflow-auto md:overflow-hidden p-2 sm:p-3 md:p-4">
      {/* Responsive grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2 sm:gap-3 lg:gap-4 h-full">
        
        {/* Mobile: Full width, Desktop: Left Column */}
        <div className="col-span-1 md:col-span-1 lg:col-span-3 flex flex-col gap-2 sm:gap-3 lg:gap-4">
          {/* Material/Product Info - Enhanced with complete specs */}
          <div className="bg-gradient-to-br from-blue-900/30 to-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-blue-500/30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1 sm:gap-2">
                <Package className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
                <span className="text-xs sm:text-sm font-bold">Current Product</span>
              </div>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-mono rounded">
                {currentMaterial?.code || 'LOADING...'}
              </span>
            </div>
            
            {currentMaterial && (
              <>
                <p className="text-xs sm:text-sm text-slate-300 mb-2 leading-relaxed">
                  {currentMaterial.description}
                </p>
                
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div className="bg-slate-900/60 rounded px-2 py-1">
                    <p className="text-slate-500 text-xs">Target</p>
                    <p className="text-cyan-400 font-bold">{currentMaterial.targetMM}mm</p>
                    <p className="text-xs text-slate-600">{currentMaterial.target.toFixed(3)}m</p>
                  </div>
                  <div className="bg-slate-900/60 rounded px-2 py-1">
                    <p className="text-slate-500 text-xs">Min</p>
                    <p className="text-emerald-400 font-bold">{currentMaterial.minMM}mm</p>
                    <p className="text-xs text-slate-600">{currentMaterial.min.toFixed(3)}m</p>
                  </div>
                  <div className="bg-slate-900/60 rounded px-2 py-1">
                    <p className="text-slate-500 text-xs">Max</p>
                    <p className="text-amber-400 font-bold">{currentMaterial.maxMM}mm</p>
                    <p className="text-xs text-slate-600">{currentMaterial.max.toFixed(3)}m</p>
                  </div>
                </div>
                
                {/* Tolerance info */}
                <div className="mt-2 pt-2 border-t border-slate-700/50">
                  <p className="text-xs text-slate-500">
                    Tolerance: ±{((currentMaterial.maxMM - currentMaterial.minMM) / 2).toFixed(1)}mm
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Connection Status */}
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-cyan-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 sm:gap-2">
                {isConnected ? (
                  <Wifi className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
                ) : (
                  <WifiOff className="h-3 w-3 sm:h-4 sm:w-4 text-red-400" />
                )}
                <span className="text-xs sm:text-xs font-semibold">API Status</span>
              </div>
              <div className={`px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs ${
                isConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {isConnected ? 'LIVE' : 'OFFLINE'}
              </div>
            </div>
            {lastUpdate && (
              <p className="text-xs text-slate-500 mt-1 sm:mt-2">
                Last: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>

          {/* Current Width */}
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-cyan-900/20 md:flex-1">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-1 sm:gap-2">
                <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400" />
                <h2 className="text-xs sm:text-sm font-semibold">Live Width</h2>
              </div>
              <button 
                onClick={fetchRealtimeData}
                className="p-0.5 sm:p-1 hover:bg-slate-700/50 rounded transition-colors"
              >
                <RefreshCw className={`h-3 w-3 text-cyan-400 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div className="text-center">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-mono font-black tracking-wider" style={{
                color: getGaugeColor(currentWidth),
                textShadow: `0 0 30px ${getGaugeColor(currentWidth)}50`
              }}>
                {currentWidth.toFixed(3)}
              </div>
              <div className="text-xs sm:text-sm text-slate-400 mt-0.5 sm:mt-1">meters</div>
              <div className="text-base sm:text-lg font-bold text-slate-300">({(currentWidth * 1000).toFixed(1)} mm)</div>
              
              <div className={`mt-2 sm:mt-3 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold inline-block ${
                isInSpec 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {isInSpec ? '✓ IN SPEC' : '✗ OUT OF SPEC'}
              </div>
            </div>
            
            {/* Tolerance Bar with Material Specs */}
            <div className="mt-3 sm:mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span className="text-xs">{(materialSpecs.min * 1000).toFixed(0)}mm</span>
                <span className="text-cyan-400 text-xs font-bold">{(materialSpecs.target * 1000).toFixed(0)}mm</span>
                <span className="text-xs">{(materialSpecs.max * 1000).toFixed(0)}mm</span>
              </div>
              <div className="relative h-4 sm:h-6 bg-gradient-to-r from-red-900/30 via-slate-800 to-red-900/30 rounded-full overflow-hidden border border-slate-700/50">
                <div className="absolute inset-y-0 left-[30%] right-[30%] bg-emerald-500/20 border-x border-emerald-500/30" />
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-500"
                  style={{
                    left: `calc(${getGaugePosition(currentWidth)}% - 4px)`,
                    backgroundColor: getGaugeColor(currentWidth),
                    boxShadow: `0 0 20px ${getGaugeColor(currentWidth)}`
                  }}
                />
              </div>
              
              {/* Deviation from target */}
              <div className="mt-2 text-center">
                <span className="text-xs text-slate-500">Deviation: </span>
                <span className={`text-xs font-bold ${
                  Math.abs(currentWidth - materialSpecs.target) < 0.002 ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {((currentWidth - materialSpecs.target) * 1000).toFixed(1)} mm
                </span>
              </div>
            </div>
          </div>

          {/* Statistics from API - Hidden on mobile, shown on tablet+ */}
          <div className="hidden sm:block bg-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-cyan-900/20">
            <h3 className="text-xs font-semibold text-slate-400 mb-2">API Statistics</h3>
            {apiData?.statistics ? (
              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs">
                <div className="bg-slate-900/60 rounded p-1.5 sm:p-2">
                  <p className="text-slate-500 text-xs">Min</p>
                  <p className="text-cyan-400 font-bold text-xs sm:text-sm">
                    {(apiData.statistics.min * 1000).toFixed(1)}mm
                  </p>
                </div>
                <div className="bg-slate-900/60 rounded p-1.5 sm:p-2">
                  <p className="text-slate-500 text-xs">Max</p>
                  <p className="text-purple-400 font-bold text-xs sm:text-sm">
                    {(apiData.statistics.max * 1000).toFixed(1)}mm
                  </p>
                </div>
                <div className="bg-slate-900/60 rounded p-1.5 sm:p-2">
                  <p className="text-slate-500 text-xs">Average</p>
                  <p className="text-emerald-400 font-bold text-xs sm:text-sm">
                    {(apiData.statistics.avg * 1000).toFixed(1)}mm
                  </p>
                </div>
                <div className="bg-slate-900/60 rounded p-1.5 sm:p-2">
                  <p className="text-slate-500 text-xs">Count</p>
                  <p className="text-amber-400 font-bold text-xs sm:text-sm">{apiData.statistics.count || 0}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No data available</p>
            )}
          </div>
        </div>

        {/* Center - Digital Twin */}
        <div className="col-span-1 md:col-span-1 lg:col-span-5 order-3 lg:order-2">
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-cyan-900/20 h-48 sm:h-64 lg:h-full">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-1 sm:gap-2">
                <Box className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400" />
                <h2 className="text-xs sm:text-sm font-semibold">Digital Twin</h2>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 text-xs text-slate-400">
                <ArrowUp className="h-2 w-2 sm:h-3 sm:w-3 text-cyan-400 animate-bounce" />
                <span className="hidden sm:inline">Material Flow</span>
                {currentMaterial && (
                  <span className="text-blue-400 font-mono text-xs ml-2">{currentMaterial.code}</span>
                )}
              </div>
            </div>
            
            <div className="relative h-[calc(100%-1.5rem)] sm:h-[calc(100%-2rem)] bg-slate-950/40 rounded-lg overflow-hidden border border-slate-800/50">
              {/* Grid Background */}
              <div className="absolute inset-0" style={{
                backgroundImage: 'linear-gradient(rgba(14,165,233,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.03) 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} />

              {/* Material Info Overlay */}
              {currentMaterial && (
                <div className="absolute top-2 right-2 bg-slate-900/80 rounded px-2 py-1 text-xs">
                  <p className="text-blue-400 font-bold">{currentMaterial.code}</p>
                  <p className="text-slate-500">Target: {currentMaterial.targetMM}mm</p>
                  <p className="text-slate-600">Range: {currentMaterial.minMM}-{currentMaterial.maxMM}mm</p>
                </div>
              )}

              {/* Scale - Hidden on mobile */}
              <div className="hidden sm:flex absolute left-2 sm:left-3 top-4 bottom-4 flex-col justify-between text-xs text-slate-500 font-mono">
                <span>{((materialSpecs.max + 0.05) * 1000).toFixed(0)}mm</span>
                <span>{(materialSpecs.target * 1000).toFixed(0)}mm</span>
                <span>{((materialSpecs.min - 0.05) * 1000).toFixed(0)}mm</span>
              </div>
              
              {/* Web Material Visualization */}
              <div className="absolute left-4 sm:left-12 right-4 top-4 bottom-4">
                {/* Tolerance zones */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 border-2 border-dashed border-emerald-500/20"
                  style={{
                    top: `${100 - ((materialSpecs.max - (materialSpecs.min - 0.05)) / 0.15) * 100}%`,
                    bottom: `${((materialSpecs.min - (materialSpecs.min - 0.05)) / 0.15) * 100}%`,
                    width: '80%',
                    background: 'rgba(16,185,129,0.02)'
                  }}
                >
                  <span className="absolute -top-5 left-0 text-xs text-emerald-400/60">Max: {(materialSpecs.max * 1000).toFixed(0)}mm</span>
                  <span className="absolute -bottom-5 left-0 text-xs text-emerald-400/60">Min: {(materialSpecs.min * 1000).toFixed(0)}mm</span>
                </div>
                
                {/* Web Material */}
                <div 
                  className="absolute top-3 bottom-3 left-1/2 -translate-x-1/2 transition-all duration-700"
                  style={{
                    width: `${((currentWidth - materialSpecs.min + 0.05) / 0.15) * 100}%`,
                    maxWidth: '90%',
                    minWidth: '20%',
                    background: isInSpec 
                      ? 'linear-gradient(180deg, rgba(6,182,212,0.15), rgba(6,182,212,0.3), rgba(6,182,212,0.15))'
                      : 'linear-gradient(180deg, rgba(239,68,68,0.15), rgba(239,68,68,0.3), rgba(239,68,68,0.15))',
                    boxShadow: isInSpec 
                      ? '0 0 20px rgba(6,182,212,0.4), inset 0 0 20px rgba(6,182,212,0.1)' 
                      : '0 0 20px rgba(239,68,68,0.4), inset 0 0 20px rgba(239,68,68,0.1)',
                    border: `1px solid ${isInSpec ? 'rgba(6,182,212,0.4)' : 'rgba(239,68,68,0.4)'}`
                  }}
                >
                  <div className="absolute inset-0 opacity-30">
                    <div 
                      className="h-full w-full"
                      style={{
                        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(255,255,255,0.05) 8px, rgba(255,255,255,0.05) 16px)',
                        animation: 'slideUp 1s linear infinite'
                      }}
                    />
                  </div>
                  
                  {/* Width Label */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <p className="text-base sm:text-xl lg:text-2xl font-bold text-white">{(currentWidth * 1000).toFixed(1)}mm</p>
                    <p className="text-xs text-slate-400 hidden sm:block">Current Width</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Charts and Alarms */}
        <div className="col-span-1 md:col-span-2 lg:col-span-4 flex flex-col gap-2 sm:gap-3 lg:gap-4 order-2 lg:order-3">
          {/* Mobile Stats Row - Only shown on mobile */}
          <div className="grid grid-cols-4 gap-1 sm:hidden">
            <div className="bg-slate-800/40 rounded p-2 text-center">
              <p className="text-xs text-slate-500">Min</p>
              <p className="text-xs font-bold text-cyan-400">{(apiData?.statistics?.min * 1000).toFixed(0) || '-'}</p>
            </div>
            <div className="bg-slate-800/40 rounded p-2 text-center">
              <p className="text-xs text-slate-500">Max</p>
              <p className="text-xs font-bold text-purple-400">{(apiData?.statistics?.max * 1000).toFixed(0) || '-'}</p>
            </div>
            <div className="bg-slate-800/40 rounded p-2 text-center">
              <p className="text-xs text-slate-500">Avg</p>
              <p className="text-xs font-bold text-emerald-400">{(apiData?.statistics?.avg * 1000).toFixed(0) || '-'}</p>
            </div>
            <div className="bg-slate-800/40 rounded p-2 text-center">
              <p className="text-xs text-slate-500">Cnt</p>
              <p className="text-xs font-bold text-amber-400">{apiData?.statistics?.count || 0}</p>
            </div>
          </div>

          {/* Trend Chart */}
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-cyan-900/20">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-green-400" />
              <h2 className="text-xs sm:text-sm font-semibold">Live Trend</h2>
              <span className="text-xs text-slate-500 ml-auto">({widthHistory.length} samples)</span>
            </div>
            
            <div className="h-20 sm:h-28 lg:h-32 relative bg-slate-950/40 rounded-lg p-1 sm:p-2">
              <svg className="w-full h-full">
                <defs>
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(14,165,233,0.05)" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
                
                {/* Dynamic spec limits based on current material */}
                {materialSpecs && (
                  <>
                    <rect 
                      x="0" 
                      y={`${100 - ((materialSpecs.max - (materialSpecs.min - 0.05)) / 0.15) * 100}%`}
                      width="100%" 
                      height={`${((materialSpecs.max - materialSpecs.min) / 0.15) * 100}%`} 
                      fill="rgba(16,185,129,0.05)" 
                    />
                    <line 
                      x1="0" 
                      y1={`${100 - ((materialSpecs.target - (materialSpecs.min - 0.05)) / 0.15) * 100}%`}
                      x2="100%" 
                      y2={`${100 - ((materialSpecs.target - (materialSpecs.min - 0.05)) / 0.15) * 100}%`}
                      stroke="rgba(16,185,129,0.2)" 
                      strokeWidth="1" 
                      strokeDasharray="2,2" 
                    />
                  </>
                )}
                
                {widthHistory.length > 1 && (
                  <>
                    <polyline
                      fill="none"
                      stroke={getGaugeColor(currentWidth)}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      points={widthHistory.map((d, i) => {
                        const x = (i / (widthHistory.length - 1)) * 100;
                        const y = 100 - ((d.width - (materialSpecs.min - 0.05)) / 0.15) * 100;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                    {/* Current point */}
                    <circle
                      cx="100%"
                      cy={`${100 - ((currentWidth - (materialSpecs.min - 0.05)) / 0.15) * 100}%`}
                      r="2"
                      fill={getGaugeColor(currentWidth)}
                      className="animate-pulse"
                    />
                  </>
                )}
                
                <text x="2" y="8" fill="rgba(148,163,184,0.4)" fontSize="7">{(materialSpecs.max * 1000).toFixed(0)}</text>
                <text x="2" y="50%" fill="rgba(148,163,184,0.4)" fontSize="7">{(materialSpecs.target * 1000).toFixed(0)}</text>
                <text x="2" y="95%" fill="rgba(148,163,184,0.4)" fontSize="7">{(materialSpecs.min * 1000).toFixed(0)}</text>
              </svg>
            </div>
          </div>

          {/* Process Info */}
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-cyan-900/20">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <Settings className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
              <h2 className="text-xs sm:text-sm font-semibold">Process Info</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Speed:</span>
                <span className="text-white font-mono">{speed} m/min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Quality:</span>
                <span className="text-emerald-400 font-bold">{qualityRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Total:</span>
                <span className="text-cyan-400 font-bold">{totalMeasurements}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Defects:</span>
                <span className="text-red-400 font-bold">{defectCount}</span>
              </div>
              <div className="flex justify-between col-span-2 pt-1 border-t border-slate-700/50">
                <span className="text-slate-500">Material:</span>
                <span className="text-blue-400 font-mono font-bold">{currentMaterial?.code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Alarms */}
          <div className="bg-slate-800/40 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-cyan-900/20 flex-1 overflow-hidden min-h-[100px]">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400" />
              <h2 className="text-xs sm:text-sm font-semibold">Violations</h2>
            </div>
            
            <div className="space-y-1 overflow-y-auto max-h-[100px] sm:max-h-[150px] lg:max-h-[200px]">
              {alarmHistory.length === 0 ? (
                <div className="text-center py-2 sm:py-4">
                  <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 text-emerald-500/30" />
                  <p className="text-xs text-slate-500">No violations</p>
                </div>
              ) : (
                alarmHistory.map(alarm => (
                  <div key={alarm.id} className="p-1.5 sm:p-2 bg-red-950/20 rounded border border-red-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-bold text-red-400">{alarm.type}</span>
                        <p className="text-xs text-slate-500">{alarm.time}</p>
                        <p className="text-xs text-blue-400 font-mono">{alarm.material}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-white">{alarm.widthMM.toFixed(1)}mm</p>
                        <p className="text-xs text-slate-600">Dev: {alarm.deviation.toFixed(1)}mm</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          0% { transform: translateY(16px); }
          100% { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WidthInspectionDashboard;